import { Add, CheckCircle, Description, ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  capitalize,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import type React from "react";
import { useEffect, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { EmptyState } from "@/components/ui";
import { DoneTasks } from "../components/qa-manager/done-tasks";
import { FileViewer } from "../components/qa-manager/file-viewer";
import { TaskList } from "../components/qa-manager/task-list";
import { TeamStatus } from "../components/qa-manager/team-status";
import { useApiQuery } from "../hooks/use-api-query.ts";
import {
  useQAManagerFiles,
  useQAManagerRun,
  useQAManagerStatus,
  useQAManagerStop,
} from "../hooks/use-qa-manager";
import type { AgentSession } from "../types/chat.ts";
import { clickableProps } from "../utils/keyboard";

const ChatPage: React.FC = () => {
  return <ChatPageInner />;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`qa-tabpanel-${index}`}
      aria-labelledby={`qa-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const ChatPageInner: React.FC = () => {
  const { data, isLoading } = useApiQuery<AgentSession[]>("/agent-sessions");
  const [selectedSession, setSelectedSession] = useState<AgentSession | null>(null);
  const [userRequest, setUserRequest] = useState("");
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [selectedFileId, setSelectedFileId] = useState<string | undefined>();
  const [tabValue, setTabValue] = useState(0);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    tasks: true,
    team: true,
    memory: true,
  });

  const runMutation = useQAManagerRun({
    onSuccess: () => {
      setTabValue(0);
      setUserRequest("");
      setSelectedFileId(undefined);
    },
  });
  const stopMutation = useQAManagerStop();

  const { data: statusData } = useQAManagerStatus(sessionId);
  const { data: filesData, isLoading: filesLoading } = useQAManagerFiles(sessionId);

  const parseUserRequest = (request: string) => {
    /*
     * It's in this format:
     *
     * ```
     * Previous User Request #1: how many tests we have for the login?
     * ---
     * Previous User Request #2: sdf asdf asdf
     * ---
     * Current Request: is there a login in the application at all?
     * ```
     *
     * And we want to extract the current request
     */

    const parts = request.split("---");
    if (parts.length < 2) return request.trim();
    const currentRequestPart = parts[parts.length - 1].trim();
    const currentRequestMatch = currentRequestPart.match(/Current Request:\s*(.*)/);
    return currentRequestMatch ? currentRequestMatch[1].trim() : request.trim();
  };

  useEffect(() => {
    if (selectedSession) {
      setSessionId(selectedSession.id);
    } else {
      setSessionId(undefined);
    }
    setUserRequest("");
    setSelectedFileId(undefined);
  }, [selectedSession]);

  // Auto-select new session after successful run
  useEffect(() => {
    if (data && sessionId && !selectedSession) {
      const newSession = data.find((session) => session.id === sessionId);
      if (newSession) {
        setSelectedSession(newSession);
      }
    }
  }, [data, sessionId, selectedSession]);

  useEffect(() => {
    // Reset tab value when status data changes
    if (statusData?.workflow_status === "done") {
      setTabValue(0);
    } else if (statusData?.workflow_status === "working") {
      setTabValue(0);
    }
  }, [statusData]);

  const handleRun = async () => {
    if (!userRequest.trim()) return;

    try {
      const result = await runMutation.mutateAsync({
        user_request: userRequest,
        session_id: sessionId,
      });
      setSessionId(result.session_id);

      // Auto-select the new session once it's available
      if (data) {
        const newSession = data.find((session) => session.id === result.session_id);
        if (newSession) {
          setSelectedSession(newSession);
        }
      }
      // Note: The runMutation onSuccess already invalidates queries to refresh the sessions list
    } catch (error) {
      console.error("Failed to start QA Manager:", error);
    }
  };

  const handleStop = (id: string) => {
    stopMutation.mutate({
      _url: `/qa-manager/sessions/${id}/stop`,
      sessionId: id,
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // useEffect(() => {
  //   if (statusData?.status == "done") {
  //     setTabValue(0);
  //   }
  // }, [statusData]);

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "idle":
        return "default";
      case "working":
      case "waiting":
        return "primary";
      case "completed":
      case "done":
        return "success";
      case "failed":
      case "errored":
        return "error";
      case "cancelled":
      case "blocked":
      case "stopped":
        return "warning";
      default:
        return "default";
    }
  };

  if (isLoading) {
    return (
      <Box data-testid="chat-inner-page">
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
          <CircularProgress size={16} />
          <Typography ml={2}>Loading chat sessions...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box data-testid="chat-inner-page" sx={{ height: "calc(100vh - 120px)", width: "100%" }}>
      <Stack spacing={2} sx={{ height: "100%" }}>
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              fullWidth
              label="What would you like the QA Manager to do?"
              value={userRequest}
              onChange={(e) => setUserRequest(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleRun();
                }
              }}
              disabled={statusData?.workflow_status === "working"}
              multiline
              maxRows={3}
            />
            <Button
              variant="outlined"
              color={statusData?.workflow_status === "working" ? "error" : "primary"}
              onClick={() =>
                statusData?.workflow_status === "working" && sessionId
                  ? handleStop(sessionId)
                  : handleRun()
              }
              disabled={runMutation.isPending || stopMutation.isPending}
              sx={{ minWidth: 120 }}
            >
              {statusData?.workflow_status !== "working"
                ? selectedSession
                  ? "Follow-up"
                  : "Ask"
                : "Stop"}
            </Button>
          </Stack>

          {sessionId && statusData && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              mt={2}
              justifyContent="space-between"
            >
              <Stack>
                <Tooltip title="Your latest request to the QA Manager">
                  <Typography variant="body1">
                    {parseUserRequest(statusData.state.user_request)}
                  </Typography>
                </Tooltip>
                <Tooltip title="Session ID">
                  <Typography variant="caption" color="text.secondary">
                    <code>{sessionId}</code>
                  </Typography>
                </Tooltip>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  icon={
                    ["working", "waiting"].includes(statusData?.workflow_status as string) ? (
                      <CircularProgress
                        size={14}
                        color={
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          getStatusColor(statusData.workflow_status) as any
                        }
                      />
                    ) : undefined
                  }
                  label={capitalize(statusData.workflow_status)}
                  color={getStatusColor(statusData.workflow_status)}
                  size="medium"
                />
              </Stack>
            </Stack>
          )}
        </Paper>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <PanelGroup direction="horizontal">
            <Panel maxSize={30} defaultSize={20} minSize={10}>
              <Paper sx={{ height: "100%", overflow: "auto", p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="h6">Recent</Typography>
                  {!!selectedSession && (
                    <Tooltip title="New Session">
                      <IconButton
                        aria-label="New Session"
                        color="primary"
                        onClick={() => {
                          setSessionId(undefined);
                          setSelectedSession(null);
                        }}
                        size="small"
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1}>
                  {data && data.length > 0 ? (
                    data.map((session) => (
                      <Paper
                        key={session.id}
                        variant="outlined"
                        sx={{
                          p: 1,
                          cursor: "pointer",
                          borderWidth: 2,
                          borderColor:
                            selectedSession?.id === session.id ? "primary.main" : undefined,
                          backgroundColor:
                            selectedSession?.id === session.id ? "action.selected" : "transparent",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                        {...clickableProps(() => setSelectedSession(session))}
                        onClick={() => setSelectedSession(session)}
                      >
                        <Stack spacing={0.5}>
                          <Tooltip title={parseUserRequest(session.state.user_request)}>
                            <Typography variant="body2" noWrap>
                              {parseUserRequest(session.state.user_request)}
                            </Typography>
                          </Tooltip>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(session.created_at), "MMM dd, yyyy HH:mm")}
                            </Typography>
                            <Chip
                              icon={
                                ["working", "waiting"].includes(session.state_status) ? (
                                  <CircularProgress
                                    size={12}
                                    color={
                                      getStatusColor(
                                        session.state_status,
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                      ) as any
                                    }
                                  />
                                ) : undefined
                              }
                              label={capitalize(session.state_status)}
                              size="small"
                              color={getStatusColor(session.state_status)}
                            />
                          </Stack>
                        </Stack>
                      </Paper>
                    ))
                  ) : (
                    <EmptyState message="No recent sessions found" />
                  )}
                </Stack>
              </Paper>
            </Panel>
            <PanelResizeHandle style={{ width: "4px" }} />

            <Panel defaultSize={50} minSize={30}>
              <Paper sx={{ height: "100%", overflow: "auto" }}>
                <Tabs
                  value={tabValue}
                  onChange={(_, newValue) => setTabValue(newValue)}
                  sx={{ borderBottom: 1, borderColor: "divider" }}
                >
                  {statusData?.state.history.tasks.some((task) => task.type === "done") && (
                    <Tab
                      label={`To Review (${statusData.state.history.tasks.filter((task) => task.type === "done").length})`}
                    />
                  )}
                  <Tab label="Progress" />
                  <Tab label="History" />
                  <Tab label="Todo" />
                </Tabs>

                {/* Done Tasks Tab - only show if there are done tasks */}
                {statusData?.state.history.tasks.some((task) => task.type === "done") && (
                  <TabPanel value={tabValue} index={0}>
                    <DoneTasks tasks={statusData.state.history.tasks} />
                  </TabPanel>
                )}

                <TabPanel
                  value={tabValue}
                  index={
                    statusData?.state.history.tasks.some((task) => task.type === "done") ? 1 : 0
                  }
                >
                  {statusData ? (
                    <Stack spacing={2}>
                      {/* Team Status */}
                      <Box>
                        <Stack
                          direction="row"
                          alignItems="center"
                          {...clickableProps(() => toggleSection("team"))}
                          onClick={() => toggleSection("team")}
                          sx={{ cursor: "pointer", mb: 1 }}
                        >
                          <IconButton
                            aria-label={
                              expandedSections.team ? "Collapse team status" : "Expand team status"
                            }
                            size="small"
                          >
                            {expandedSections.team ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          <Typography variant="h6">
                            Team Status ({statusData.state.team.members.length})
                          </Typography>
                        </Stack>
                        <Collapse in={expandedSections.team}>
                          <TeamStatus
                            team={statusData.state.team}
                            tasks={statusData.state.history.tasks}
                          />
                        </Collapse>
                      </Box>

                      <Divider />

                      {/* Current Tasks */}
                      <Box>
                        <Stack
                          direction="row"
                          alignItems="center"
                          {...clickableProps(() => toggleSection("tasks"))}
                          onClick={() => toggleSection("tasks")}
                          sx={{ cursor: "pointer", mb: 1 }}
                        >
                          <IconButton
                            aria-label={
                              expandedSections.tasks ? "Collapse team tasks" : "Expand team tasks"
                            }
                            size="small"
                          >
                            {expandedSections.tasks ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          <Typography variant="h6">Team tasks</Typography>
                        </Stack>
                        <Collapse in={expandedSections.tasks}>
                          <TaskList tasks={statusData.state.history.tasks} />
                        </Collapse>
                      </Box>

                      <Divider />

                      {/* Memory/Thinking */}
                      <Box>
                        <Stack
                          direction="row"
                          alignItems="center"
                          {...clickableProps(() => toggleSection("memory"))}
                          onClick={() => toggleSection("memory")}
                          sx={{ cursor: "pointer", mb: 1 }}
                        >
                          <IconButton
                            aria-label={
                              expandedSections.memory
                                ? "Collapse agent thinking"
                                : "Expand agent thinking"
                            }
                            size="small"
                          >
                            {expandedSections.memory ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          <Typography variant="h6">Agent Thinking</Typography>
                        </Stack>
                        <Collapse in={expandedSections.memory}>
                          {statusData.state.history.memories.length > 0 ? (
                            <Stack spacing={1}>
                              {statusData.state.history.memories
                                .sort((a, b) => b.ts - a.ts)
                                .map((memory, idx) => (
                                  <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Step {memory.ts}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                      <strong>Thinking:</strong> {memory.thinking}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                      <strong>Next Goal:</strong> {memory.next_goal}
                                    </Typography>
                                  </Paper>
                                ))}
                            </Stack>
                          ) : (
                            <EmptyState message="No thoughts recorded yet" />
                          )}
                        </Collapse>
                      </Box>
                    </Stack>
                  ) : (
                    <Alert severity="info">Enter a request to start the QA Manager Agent</Alert>
                  )}
                </TabPanel>

                <TabPanel
                  value={tabValue}
                  index={
                    statusData?.state.history.tasks.some((task) => task.type === "done") ? 2 : 1
                  }
                >
                  {statusData ? (
                    <Stack spacing={2} divider={<Divider />}>
                      <Alert severity="info">
                        {statusData.state.user_request.split("---").map((part, idx) => (
                          <Typography
                            key={idx}
                            variant="body1"
                            sx={{
                              whiteSpace: "pre-wrap",
                              mb:
                                idx < statusData.state.user_request.split("---").length - 1 ? 1 : 0,
                            }}
                          >
                            {part.trim()}
                          </Typography>
                        ))}
                      </Alert>

                      {statusData.state.history.logs.length > 0 && (
                        <Stack spacing={1} alignItems="stretch">
                          <Typography variant="subtitle1" gutterBottom>
                            Logs
                          </Typography>
                          <Stack spacing={1} alignItems="stretch">
                            {statusData.state.history.logs
                              .sort((a, b) => b.ts - a.ts)
                              .map((log, idx) => (
                                <Alert
                                  key={idx}
                                  severity={log.level === "error" ? "error" : "info"}
                                >
                                  <Stack direction="column" spacing={1}>
                                    <Typography variant="body2">{log.message}</Typography>
                                    <Stack alignItems="flex-start" width="100%">
                                      <Typography variant="caption" color="text.secondary">
                                        at step {log.ts}
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                </Alert>
                              ))}
                          </Stack>
                        </Stack>
                      )}
                    </Stack>
                  ) : (
                    <Alert severity="info">Enter a request to start the QA Manager Agent</Alert>
                  )}
                </TabPanel>

                <TabPanel
                  value={tabValue}
                  index={
                    statusData?.state.history.tasks.some((task) => task.type === "done") ? 3 : 2
                  }
                >
                  {statusData ? (
                    <Stack spacing={2}>
                      <List dense>
                        {statusData.state.history.todo.items
                          .filter((item) => !item.deleted)
                          .sort((a, b) => a.order - b.order)
                          .map((item) => (
                            <ListItem
                              key={item.order}
                              sx={{
                                py: 0.5,
                                px: 1,
                                borderRadius: 1,
                                mb: 0.5,
                                backgroundColor: item.done ? "action.selected" : "transparent",
                                "&:hover": {
                                  backgroundColor: item.done ? "action.selected" : "action.hover",
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                {item.done ? (
                                  <CheckCircle color="success" fontSize="small" />
                                ) : (
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      border: 2,
                                      borderColor: "primary.main",
                                      borderRadius: "2px",
                                      backgroundColor: "transparent",
                                    }}
                                  />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      textDecoration: item.done ? "line-through" : "none",
                                      color: item.done ? "text.secondary" : "text.primary",
                                    }}
                                  >
                                    {item.order}. {item.text}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        {statusData.state.history.todo.items.filter((item) => !item.deleted)
                          .length === 0 && <EmptyState message="📋 No todo items yet" />}
                      </List>
                    </Stack>
                  ) : (
                    <Alert severity="info">Enter a request to start the QA Manager Agent</Alert>
                  )}
                </TabPanel>
              </Paper>
            </Panel>

            <PanelResizeHandle style={{ width: "4px" }} />

            <Panel defaultSize={30} minSize={20}>
              <Paper sx={{ height: "100%", overflow: "auto", p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Assets
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {filesLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  (() => {
                    const nonTodoFiles = filesData?.filter((file) => file.name !== "todo.md") || [];
                    return nonTodoFiles.length > 0 ? (
                      <Stack spacing={1}>
                        {nonTodoFiles.map((file) => (
                          <Paper
                            key={file.id}
                            variant="outlined"
                            sx={{
                              p: 1,
                              cursor: "pointer",
                              backgroundColor:
                                selectedFileId === file.id ? "action.selected" : "transparent",
                              "&:hover": {
                                backgroundColor: "action.hover",
                              },
                            }}
                            {...clickableProps(() =>
                              setSelectedFileId(selectedFileId === file.id ? undefined : file.id),
                            )}
                            onClick={() =>
                              setSelectedFileId(selectedFileId === file.id ? undefined : file.id)
                            }
                          >
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Description fontSize="small" />
                              <Typography variant="body2" noWrap>
                                {file.name}
                              </Typography>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <EmptyState message="No files created yet" />
                    );
                  })()
                )}

                {selectedFileId && sessionId && (
                  <Box mt={3}>
                    <Divider sx={{ mb: 2 }} />
                    <FileViewer sessionId={sessionId} fileId={selectedFileId} />
                  </Box>
                )}
              </Paper>
            </Panel>
          </PanelGroup>
        </Box>
      </Stack>
    </Box>
  );
};

export default ChatPage;
